import {DestroyRef, inject, Injectable} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {ActivatedRoute, ParamMap, Router} from "@angular/router"
import {
    AssetFilterInput,
    AssetState,
    ContentTypeModel,
    DataObjectFilterInput,
    DataObjectState,
    HdriFilterInput,
    IntInFilter,
    JobFarmInstanceFilterInput,
    JobFarmInstanceState,
    JobFilterInput,
    JobState,
    JobType,
    MaterialColor,
    MaterialFilterInput,
    MaterialPattern,
    MaterialState,
    MaterialType,
    ModelFilterInput,
    ModelState,
    NextActor,
    OrganizationFilterInput,
    OrganizationOrderByCriteria,
    OrganizationOrderByInput,
    PaymentState,
    PictureFilterInput,
    PictureState,
    SortOrder,
    TagFilterInput,
    TagType,
    TemplateFilterInput,
    TemplateState,
    TemplateType,
    TextureGroupFilterInput,
    UserFilterInput,
} from "@generated"
import {ProductTypeLabels} from "@app/platform/models/state-labels/product-type-labels"
import {SceneTypeLabels} from "@app/platform/models/state-labels/scene-type-labels"
import {filtersAreEqual} from "@common/helpers/filters"
import {midnightThisMorning, midnightTonight} from "@common/models/date/date"
import {AuthService} from "@common/services/auth/auth.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {
    AnyStateType,
    AssetStateLabels,
    DataObjectStateLabels,
    MaterialStateLabels,
    ModelStateLabels,
    NextActorLabels,
    PaymentStateLabels,
    PictureStateLabels,
    TagTypeOptions,
    TemplateStateLabels,
    TemplateTypeLabels,
} from "@labels"
import {MaterialColorLabels} from "@app/platform/models/state-labels/material-color-labels"
import {MaterialPatternLabels} from "@app/platform/models/state-labels/material-pattern-labels"
import {MaterialTypeLabels} from "@app/platform/models/state-labels/material-type-labels"
import {JobFarmInstanceStateLabels} from "@platform/models/state-labels/job-farm-instance-state-labels"
import {JobQueueGroupFilterValues, JobQueueGroupLabels} from "@platform/models/state-labels/job-queue-group-labels"
import {JobTaskTypeFilterValues, JobTaskTypeLabels} from "@platform/models/state-labels/job-task-type-labels"
import {JobTypeLabels} from "@platform/models/state-labels/job-type-labels"
import {LastSeenLabels} from "@platform/models/state-labels/last-seen-labels"
import {distinctUntilChanged, map, Observable, ReplaySubject, shareReplay, startWith} from "rxjs"
import {deepEqual} from "@cm/utils"

const BooleanOptions = (falseOption: string, trueOption: string) =>
    new Map([
        ["0", {label: falseOption}],
        ["1", {label: trueOption}],
    ])

export const FilterDefinitions = {
    accountType: {
        label: "Account Type",
        options: BooleanOptions("Manual creation", "Self service"),
    },
    assetId: {},
    assetState: {label: "State", options: AssetStateLabels},
    assetsCompleted: {
        label: "Assets",
        options: BooleanOptions("Not completed", "Completed"),
    },
    assignedToContentType: {},
    assignedToId: {},
    dataObjectState: {label: "State", options: DataObjectStateLabels},
    dateFrom: {},
    dateTo: {},
    hasAssignedUser: {
        label: "Availability",
        options: BooleanOptions("Not assigned", "Assigned"),
    },
    hasCyclesMaterial: {
        label: "Cycles Material",
        options: BooleanOptions("Not available", "Available"),
    },
    includeInactive: {},
    isActive: {
        label: "UserActive",
        options: BooleanOptions("Disabled", "Enabled"),
    },
    isRelated: {
        label: "Relation to other files",
        options: BooleanOptions("Related", "Not related"),
    },
    isStaff: {
        label: "User Type",
        options: BooleanOptions("Non-staff", "Staff"),
    },
    jobFarmInstanceState: {label: "State", options: JobFarmInstanceStateLabels},
    jobQueueGroup: {label: "Queue Group", options: JobQueueGroupLabels},
    jobTaskType: {label: "Task Type", options: JobTaskTypeLabels},
    jobType: {label: "Job Type", options: JobTypeLabels},
    lastSeen: {label: "Last seen", options: LastSeenLabels},
    legacyId: {},
    materialColors: {label: "Color", options: MaterialColorLabels},
    materialId: {},
    materialPatterns: {label: "Pattern", options: MaterialPatternLabels},
    materialState: {label: "State", options: MaterialStateLabels},
    materialTypes: {label: "Material Type", options: MaterialTypeLabels},
    meshSpecific: {
        label: "Usage",
        options: BooleanOptions("General", "Mesh-specific"),
    },
    model: {
        label: "Model",
    },
    onboardingCompleted: {},
    modelState: {label: "State", options: ModelStateLabels},
    nextActor: {label: "Up Next", options: NextActorLabels},
    organizationId: {label: "Customer", kind: "separate"},
    paymentState: {label: "Payment State", options: PaymentStateLabels},
    pictureState: {label: "State", options: PictureStateLabels},
    public: {
        label: "Visibility",
        options: BooleanOptions("Public", "Private"),
    },
    publicDownloads: {label: "Public Library", kind: "separate"},
    relatedToId: {label: "Related to", kind: "separate"},
    sampleArrived: {
        label: "Sample",
        options: BooleanOptions("Not available", "Available"),
    },
    scannedByNone: {},
    scannedByUserId: {},
    setId: {},
    sort: {},
    state: {},
    projectId: {label: "Project"},
    tagId: {label: "Tag"},
    tagType: {label: "Tag Type", options: TagTypeOptions},
    templateState: {label: "State", options: TemplateStateLabels},
    templateType: {label: "Template Type", options: TemplateTypeLabels},
    productType: {label: "Product Type", options: ProductTypeLabels},
    sceneType: {label: "Scene Type", options: SceneTypeLabels},
    userId: {label: "User"},
}

export type FilterNameType = keyof typeof FilterDefinitions
export const FilterNames = Object.keys(FilterDefinitions) as FilterNameType[]

export type FilterCriterionState = {
    [name: string]: Set<string>
}

export type FilterStates = {search: string | undefined; criteria: FilterCriterionState}

export const checkFilterCriterionStatesEqual = (criteria1: FilterCriterionState, criteria2: FilterCriterionState) => {
    const checkSetsEqual = (set1: Set<string>, set2: Set<string>) => {
        const array1 = Array.from(set1)
        const array2 = Array.from(set2)
        return array1.every((value) => set2.has(value)) && array2.every((value) => set1.has(value))
    }

    return (
        Object.entries(criteria1).every(([key, value]) => criteria2[key] && checkSetsEqual(value, criteria2[key])) &&
        Object.entries(criteria2).every(([key, value]) => criteria1[key] && checkSetsEqual(value, criteria1[key]))
    )
}

const booleanFilter = (values?: Set<string>) => {
    if (!values) {
        return undefined
    }
    if (values.has("0")) {
        if (values.has("1")) {
            return undefined
        } else {
            return false
        }
    } else {
        if (values.has("1")) {
            return true
        } else {
            return undefined
        }
    }
}

export const arrayFilter = (values: Set<string> | undefined) => {
    if (!values?.size) {
        return undefined
    }
    return Array.from(values)
}

// Expands selected filter keys into the concrete values they cover. Batched options
// (e.g. "Utility", "Power Machine") map to several values; unknown keys pass through.
const expandFilter = (values: Set<string> | undefined, expansions: Record<string, string[]>) => {
    if (!values?.size) {
        return undefined
    }
    return Array.from(values).flatMap((value) => expansions[value] ?? [value])
}

const stringFilter = (values: Set<string> | undefined) => {
    if (!values?.size || values.size > 1) {
        return undefined
    }
    return Array.from(values)[0]
}

export const stringInFilter = (values: Set<string> | undefined) => {
    if (!values?.size) {
        return undefined
    }
    return {in: Array.from(values)}
}

const intInFilter = (values: Set<number | string> | undefined): IntInFilter | undefined => {
    if (!values?.size) {
        return undefined
    }
    return {in: Array.from(values).map((value) => Number(value))}
}

const dateFilter = (values: Set<string> | undefined, defaultValue?: Date) => {
    if (!values?.size) {
        return defaultValue ?? new Date()
    }
    return new Date(Array.from(values)[0])
}

/**
 * Parses the query parameters into a filter object ready to be used in GraphQL queries.
 */
@Injectable({
    providedIn: "root",
})
export class FiltersService {
    public currentStates: FilterStates = {search: "", criteria: {}}
    public states = new ReplaySubject<FilterStates>()

    readonly auth = inject(AuthService)
    readonly permission = inject(PermissionsService)
    $can = this.permission.$to

    readonly destroyRef = inject(DestroyRef)
    readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)

    constructor() {
        this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef), map(this.parseURLFilters)).subscribe((states) => {
            if (!deepEqual(this.currentStates, states)) {
                this.currentStates = states
                this.states.next(states)
            }
        })
    }

    // we show all public items to non-staff users when no organization is selected
    defaultPublicFilter(states: FilterStates) {
        if (this.$can().read.organization() || states.criteria.organizationId) {
            return undefined
        } else {
            return true
        }
    }

    public updateSearchText = async (searchText: string) => {
        await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {search: searchText || undefined},
            queryParamsHandling: "merge",
        })
    }

    public updateCriteria = async (filterName: string, values: Iterable<string>) => {
        await this.updateMultipleCriteria({[filterName]: values})
    }

    public updateMultipleCriteria = async (criteria: Record<string, Iterable<string>>) => {
        // TODO: maybe sort the values? then again, probably not necessary - we don't sort the query parameters, after all...
        const queryParams: Record<string, string | null | undefined> = {page: null}
        for (const [filterName, values] of Object.entries(criteria)) {
            queryParams[filterName] = Array.from(values).join(",") || undefined
        }
        await this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: "merge",
        })
    }

    private parseURLFilters = (params: ParamMap): FilterStates => {
        const result: FilterStates = {
            search: params.get("search") ?? undefined,
            criteria: {},
        }
        params.keys.forEach((key) => {
            if (key in FilterDefinitions) {
                // TODO: maybe parse using something like zod to ensure that the values are valid (?)
                const values = params.getAll(key).flatMap((value) => value.split(","))
                // Filter out empty strings to handle deselected filters correctly
                const filteredValues = values.filter((value) => value !== "")
                if (filteredValues.length > 0) {
                    result.criteria[key as keyof typeof FilterDefinitions] = new Set(filteredValues as AnyStateType[])
                }
            }
        })

        return result
    }

    public makeObservable$: <FilterType>(filterGetter: (states: FilterStates) => FilterType) => Observable<FilterType> = (filterGetter) =>
        this.route.queryParamMap.pipe(
            takeUntilDestroyed(this.destroyRef),
            map(this.parseURLFilters),
            distinctUntilChanged(
                (previous, current) => previous.search === current.search && checkFilterCriterionStatesEqual(previous.criteria, current.criteria),
            ),
            map((states) => filterGetter(states)),
            shareReplay({bufferSize: 0, refCount: true}),
        )

    public assetFilter = (): AssetFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            hasAssignedUser: booleanFilter(states.criteria.hasAssignedUser),
            nextActor: arrayFilter(states.criteria.nextActor) as NextActor[],
            materialId: stringInFilter(states.criteria.materialId),
            modelId: stringInFilter(states.criteria.model),
            organizationId: stringInFilter(states.criteria.organizationId),
            search: states.search ?? undefined,
            state: arrayFilter(states.criteria.assetState) as AssetState[],
            userId: stringInFilter(states.criteria.userId),
        }
    }

    public dataObjectFilter = (): DataObjectFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            assignedToContentType: stringFilter(states.criteria.assignedToContentType) as ContentTypeModel | undefined,
            assignedToId: stringFilter(states.criteria.assignedToId),
            isRelated: booleanFilter(states.criteria.isRelated),
            relatedTo: states.criteria.relatedToId ? {in: Array.from(states.criteria.relatedToId)} : undefined,
            state: arrayFilter(states.criteria.dataObjectState) as DataObjectState[],
        }
    }

    public jobFarmInstanceFilter = (): JobFarmInstanceFilterInput => {
        const states = this.currentStates
        // Last seen filter
        const lastSeenFilter = () => {
            const lastSeenSet = states.criteria.lastSeen
            if (lastSeenSet?.size) {
                const lastSeenFilter = Array.from(lastSeenSet)[0]
                const now = new Date()
                const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

                if (lastSeenFilter === "today") {
                    return {gte: startOfToday.toISOString()}
                } else if (lastSeenFilter === "before") {
                    return {lt: startOfToday.toISOString()}
                }
            }
            return undefined
        }
        return {
            state: arrayFilter(states.criteria.jobFarmInstanceState) as JobFarmInstanceState[] | undefined,
            lastReport: lastSeenFilter(),
        }
    }

    public jobFarmInstanceFilter$: Observable<JobFarmInstanceFilterInput> = this.makeObservable$(this.jobFarmInstanceFilter)

    public jobFilter = (states = this.currentStates): JobFilterInput => {
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            createdByIds: stringInFilter(states.criteria.userId),
            jobType: arrayFilter(states.criteria.jobType) as JobType[] | undefined,
            organizationId: stringInFilter(states.criteria.organizationId),
            queueGroup: expandFilter(states.criteria.jobQueueGroup, JobQueueGroupFilterValues) ?? undefined,
            search: states.search ?? undefined,
            state: arrayFilter(states.criteria.state) as JobState[],
            taskType: expandFilter(states.criteria.jobTaskType, JobTaskTypeFilterValues) ?? undefined,
        }
    }

    public jobFilter$: Observable<JobFilterInput> = this.makeObservable$(this.jobFilter)

    public hdriFilter = (): HdriFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            search: states.search ?? undefined,
            organizationId: states.criteria.organizationId?.size ? {in: Array.from(states.criteria.organizationId)} : undefined,
        }
    }

    public materialFilter = (states = this.currentStates): MaterialFilterInput => {
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            colors: arrayFilter(states.criteria.materialColors) as MaterialColor[],
            hasAssignedUser: booleanFilter(states.criteria.hasAssignedUser),
            hasCyclesMaterial: booleanFilter(states.criteria.hasCyclesMaterial),
            legacyId: intInFilter(states.criteria.legacyId),
            materialTypes: arrayFilter(states.criteria.materialTypes) as MaterialType[],
            meshSpecific: booleanFilter(states.criteria.meshSpecific),
            nextActor: arrayFilter(states.criteria.nextActor) as NextActor[],
            organizationId: stringInFilter(states.criteria.organizationId),
            patterns: arrayFilter(states.criteria.materialPatterns) as MaterialPattern[],
            paymentState: arrayFilter(states.criteria.paymentState) as PaymentState[],
            public: booleanFilter(states.criteria.public),
            publicDownloads: booleanFilter(states.criteria.publicDownloads),
            sampleArrived: booleanFilter(states.criteria.sampleArrived),
            scannedByNone: booleanFilter(states.criteria.scannedByNone),
            scannedByUserId: stringInFilter(states.criteria.scannedByUserId),
            search: states.search ?? undefined,
            state: arrayFilter(states.criteria.materialState) as MaterialState[],
            tagId: stringInFilter(states.criteria.tagId),
            userId: stringInFilter(states.criteria.userId),
        }
    }
    public materialFilter$ = this.makeObservable$(this.materialFilter)

    public materialLibraryFilter = (): MaterialFilterInput => {
        const {meshSpecific: _, ...rest} = this.materialFilter()
        return {...rest, meshSpecific: false}
    }

    public modelFilter = (): ModelFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            hasAssignedUser: booleanFilter(states.criteria.hasAssignedUser),
            legacyId: intInFilter(states.criteria.legacyId),
            nextActor: arrayFilter(states.criteria.nextActor) as NextActor[],
            organizationId: stringInFilter(states.criteria.organizationId),
            paymentState: arrayFilter(states.criteria.paymentState) as PaymentState[],
            public: booleanFilter(states.criteria.public),
            search: states.search ?? undefined,
            state: arrayFilter(states.criteria.modelState) as ModelState[],
            tagId: stringInFilter(states.criteria.tagId),
            userId: stringInFilter(states.criteria.userId),
        }
    }

    public organizationFilter = (): OrganizationFilterInput => {
        const states = this.currentStates
        const includeInactive = states.criteria.includeInactive?.has("1")
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return includeInactive ? {} : {isActive: true}
        }
        return {
            isActive: includeInactive ? undefined : true,
            isSelfService: booleanFilter(states.criteria.accountType),
            search: states.search ?? undefined,
        }
    }

    // Maps the `sort` query param to a GraphQL ordering for the organisations list.
    // `Id asc` is appended as a stable tiebreaker (matching the backend default), and
    // `undefined` is returned when nothing is selected so the backend default applies.
    public organizationOrderBy = (): OrganizationOrderByInput[] | undefined => {
        const code = stringFilter(this.currentStates.criteria.sort)
        const orderByForCode: Record<string, OrganizationOrderByInput> = {
            name_asc: {key: OrganizationOrderByCriteria.Name, direction: SortOrder.Asc},
            name_desc: {key: OrganizationOrderByCriteria.Name, direction: SortOrder.Desc},
            created_desc: {key: OrganizationOrderByCriteria.CreatedAt, direction: SortOrder.Desc},
            created_asc: {key: OrganizationOrderByCriteria.CreatedAt, direction: SortOrder.Asc},
            active_desc: {key: OrganizationOrderByCriteria.LastActiveAt, direction: SortOrder.Desc},
            active_asc: {key: OrganizationOrderByCriteria.LastActiveAt, direction: SortOrder.Asc},
        }
        const entry = code ? orderByForCode[code] : undefined
        return entry ? [entry, {key: OrganizationOrderByCriteria.Id, direction: SortOrder.Asc}] : undefined
    }

    public statisticsFilter$: Observable<{from: Date; to: Date}> = this.route.queryParamMap.pipe(
        takeUntilDestroyed(this.destroyRef),
        map(this.parseURLFilters),
        distinctUntilChanged(filtersAreEqual),
        map((states) => {
            return {
                from: dateFilter(states.criteria.dateFrom, midnightThisMorning()),
                to: dateFilter(states.criteria.dateTo, midnightTonight()),
            }
        }),
    )

    public tagFilter = (): TagFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            search: states.search ?? undefined,
            organizationId: stringInFilter(states.criteria.organizationId),
            tagType: arrayFilter(states.criteria.tagType) as TagType[],
        }
    }

    public tagFilter$: Observable<TagFilterInput> = this.route.queryParamMap.pipe(
        takeUntilDestroyed(this.destroyRef),
        map(this.parseURLFilters),
        distinctUntilChanged(filtersAreEqual),
        map((states) => {
            if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
                return {}
            }
            return {
                search: states.search ?? undefined,
                organizationId: stringInFilter(states.criteria.organizationId),
                tagType: arrayFilter(states.criteria.tagType) as TagType[],
            }
        }),
        startWith({}),
    )

    public pictureFilter = (): PictureFilterInput => {
        const states = this.currentStates
        const organizationIds = states.criteria.organizationId
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {
                organizationId: stringInFilter(organizationIds),
            }
        }
        return {
            assetId: stringInFilter(states.criteria.assetId),
            assetsCompleted: booleanFilter(states.criteria.assetsCompleted),
            hasAssignedUser: booleanFilter(states.criteria.hasAssignedUser),
            nextActor: arrayFilter(states.criteria.nextActor) as NextActor[],
            state: arrayFilter(states.criteria.pictureState) as PictureState[],
            userId: stringInFilter(states.criteria.userId),
            organizationId: stringInFilter(organizationIds),
            paymentState: arrayFilter(states.criteria.paymentState) as PaymentState[],
            setId: stringInFilter(states.criteria.setId),
            projectId: stringInFilter(states.criteria.projectId),
            search: states.search ?? undefined,
            tagId: stringInFilter(states.criteria.tagId),
        }
    }

    public templateFilter = (): TemplateFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {
                type: [TemplateType.Part, TemplateType.Product, TemplateType.Scene, TemplateType.Picture, TemplateType.System],
            }
        }
        return {
            public: booleanFilter(states.criteria.public),
            organizationId: stringInFilter(states.criteria.organizationId),
            paymentState: arrayFilter(states.criteria.paymentState) as PaymentState[],
            search: states.search ?? undefined,
            state: arrayFilter(states.criteria.templateState) as TemplateState[],
            tagId: stringInFilter(states.criteria.tagId),
            type: (arrayFilter(states.criteria.templateType) ?? [
                TemplateType.Part,
                TemplateType.Product,
                TemplateType.Scene,
                TemplateType.Picture,
                TemplateType.System,
            ]) as TemplateType[],
        }
    }

    public productFilter = (): TemplateFilterInput => {
        const {type, ...rest} = this.templateFilter()
        const productTypes = [...ProductTypeLabels.values()]
        return {...rest, type: type?.filter((t) => productTypes.some((x) => x.state === t))}
    }

    public sceneFilter = (): TemplateFilterInput => {
        const {type, ...rest} = this.templateFilter()
        const sceneTypes = [...SceneTypeLabels.values()]
        return {...rest, type: type?.filter((t) => sceneTypes.some((x) => x.state === t))}
    }

    public textureGroupFilter = (): TextureGroupFilterInput => {
        const states = this.currentStates
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return {}
        }
        return {
            search: states.search ?? undefined,
            organizationId: states.criteria.organizationId?.size ? {in: Array.from(states.criteria.organizationId)} : undefined,
        }
    }

    public userFilter = (): UserFilterInput => {
        const states = this.currentStates
        const includeInactive = states.criteria.includeInactive?.has("1")
        if (!states.search && Object.values(states.criteria).every((state) => !state?.size)) {
            return includeInactive ? {} : {isActive: true}
        }
        return {
            isActive: includeInactive ? undefined : true,
            isStaff: booleanFilter(states.criteria.isStaff),
            onboardingCompleted: booleanFilter(states.criteria.onboardingCompleted),
            organizationId: states.criteria.organizationId?.size ? {in: Array.from(states.criteria.organizationId)} : undefined,
            search: states.search ?? undefined,
            selfServiceSignup: booleanFilter(states.criteria.accountType),
        }
    }
}
